import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { GrFormClose } from "react-icons/gr";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Hidden,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useFetchAllAppUserTypeQuery } from "../../../features/admin/AppUserType/appUserTypeApi";
import { useUpdateVendorMutation } from "../../../features/admin/vendor/vendorApi";
import PointSharing from "./PointSharing";
import RedemptionApprovalHierarchay from "./RedemptionApprovalHierarchay";

const DreamGiftValue = { users: [], target: "point", percentage: null };

const addOnFeatures = {
  registration_bonus: false,
  referral: false,
  bonus_system: false,
  campaign: false,
  membership: false,
  scheme_membership: false,
  scheme: false,
  sales_booster: false,
  scratch_coupon: false,
  dream_gift: false,
  kyc_online_verification: false,
  instant_payment: false,
  multi_pop_on_qr: false,
  qr_rescan: false,
  kafka_scan: false,
  tds_calculation: false,
  tds_deducted: false,
  tds_compliance: false,
  gift_auto_approval: false,
  aadhar_gender_dob: false,
  point_expire: false,
  scanning_type: "main",
  loyalty_rules_type: "main",
  monthly_tds: { taken: false, only_scheme: false },
  reserve_tds: {
    required_documents: {},
    tds_reserve_percentage: {
      before_document_submission: 10,
      after_document_submission: 20,
    },
  },
};

const pointsSharingObject = {
  flat_points: true,
  percentage_points: false,
  is_point_sharing: false,
  percentage_points_value: null,
  is_point_sharing_bw_user: false,
  is_point_reserved: false,
  point_reserved_percentage: null,
  point_sharing_bw_user: { user: {}, percentage: {} },
};

const RedemptionHierarchy = {
  gift_mapped_users: {},
  is_mapped_users: false,
  gift_extra_approvals: {},
};

const BOOLEAN_ADDON_KEYS = [
  "registration_bonus",
  "referral",
  "bonus_system",
  "campaign",
  "membership",
  "scheme_membership",
  "scheme",
  "sales_booster",
  "kyc_online_verification",
  "instant_payment",
  "tds_calculation",
  "tds_deducted",
  "tds_compliance",
  "gift_auto_approval",
  "aadhar_gender_dob",
  "point_expire",
  "scratch_coupon",
];

const SCAN_SECTION_TOGGLES = [
  { key: "kafka_scan", label: "Kafka Scan" },
  { key: "qr_rescan", label: "QR Rescan" },
  { key: "multi_pop_on_qr", label: "Multi Pop On QR" },
];

const MAIN_SCHEME_TYPE_FIELDS = [
  { key: "scanning_type", label: "Scanning Type" },
  { key: "loyalty_rules_type", label: "Loyalty Rules Type" },
];

const MAIN_SCHEME_OPTIONS = ["main", "scheme", "both"];

const RulesAndFeatures = () => {
  const [vendorsList, setVendorsList] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(0);
  const [selectedVendorData, setSelectedVendorData] = useState(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState(0);
  const [selectedIndustryName, setSelectedIndustryName] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [updateDisabled, setUpdateDisabled] = useState(true);

  const [addOnOptions, setAddOnOptions] = useState(addOnFeatures);
  const [dreamGiftData, setDreamGiftData] = useState(DreamGiftValue);
  const [dreamGiftPopUp, setDreamGiftPopUp] = useState(false);
  const [pointSharingPopUp, setPointSharingPopUp] = useState(false);
  const [pointsSharing, setPointsSharing] = useState(pointsSharingObject);
  const [redemptionHierarchyPopUp, setRedemptionHierarchyPopUp] = useState(false);
  const [redemptionHierarchy, setRedemptionHierarchy] = useState(RedemptionHierarchy);
  const [reserveTdsPopUp, setReserveTdsPopUp] = useState(false);

  const { data: allAppUserTypeData } = useFetchAllAppUserTypeQuery({
    token: sessionStorage.getItem("access_token"),
  });
  const [updateVendor, { data: updateVendorData, error: updateVendorError }] =
    useUpdateVendorMutation();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/admin/company`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("access_token")}` },
      })
      .then((res) => setVendorsList(res.data.body || []))
      .catch(console.log);

    axios
      .get(`${process.env.REACT_APP_BASE_URL}/admin/industry`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => setIndustryList(res.data.body))
      .catch(console.log);
  }, []);

  useEffect(() => {
    setUpdateDisabled(!(selectedIndustryId != 0 && selectedVendor != 0));
  }, [selectedIndustryId, selectedVendor]);

  useEffect(() => {
    if (updateVendorData) toast.success(updateVendorData.message || "Updated");
    if (updateVendorError) toast.error("Something went wrong");
  }, [updateVendorData, updateVendorError]);

  useEffect(() => {
    if (!selectedVendorData) {
      setAddOnOptions(addOnFeatures);
      setDreamGiftData(DreamGiftValue);
      setPointsSharing(pointsSharingObject);
      setRedemptionHierarchy(RedemptionHierarchy);
      return;
    }
    setAddOnOptions({ ...addOnFeatures, ...(selectedVendorData.addon_features || {}) });
    setPointsSharing(selectedVendorData.points_sharing || pointsSharingObject);
    setRedemptionHierarchy(
      selectedVendorData.redemption_hierarchy || RedemptionHierarchy,
    );
    setDreamGiftData(
      selectedVendorData.dream_gift == null
        ? DreamGiftValue
        : selectedVendorData.dream_gift,
    );
    if (selectedVendorData.user_types) setUserTypes([...selectedVendorData.user_types]);
  }, [selectedVendorData]);

  const handleVendorChange = (e) => {
    const id = e.target.value;
    setSelectedVendor(id);
    if (id === "0" || id === 0) {
      setSelectedVendorData(null);
      setSelectedIndustryId(0);
      setSelectedIndustryName("");
      setUserTypes([]);
      return;
    }
    const vendor = vendorsList.find((v) => String(v.company_id) === String(id));
    if (!vendor) return;
    setSelectedVendorData(vendor);
    setSelectedIndustryId(vendor.industry_id || 0);
    setSelectedIndustryName(vendor.industry_name || "");
    setUserTypes(vendor.user_types ? [...vendor.user_types] : []);
  };

  const handleIndustryChange = (e) => {
    const id = e.target.value;
    setSelectedIndustryId(id);
    if (id === "0" || id === 0) {
      setSelectedIndustryName("");
      setUserTypes([]);
      return;
    }
    const industry = industryList.find((i) => String(i.industry_id) === String(id));
    if (industry) {
      setSelectedIndustryName(industry.name);
      if (industry.users) setUserTypes([...industry.users]);
    }
  };

  const handleAddUserType = (e) => {
    const value = e.target.value;
    if (!value || userTypes.includes(value)) return;
    setUserTypes([...userTypes, value]);
  };

  const handleAddOnToggle = (type) => {
    const copied = { ...addOnOptions };
    copied[type] = copied[type] ? false : true;
    setAddOnOptions(copied);
    if (type === "dream_gift") setDreamGiftPopUp(true);
  };

  const handleMainSchemeType = (key, value) => {
    if (key === "scanning_type") {
      const membershipFlags =
        value === "main"
          ? { membership: true, scheme_membership: false }
          : value === "scheme"
            ? { membership: false, scheme_membership: true }
            : { membership: true, scheme_membership: true }; // "both"
      setAddOnOptions({ ...addOnOptions, [key]: value, ...membershipFlags });
    } else {
      setAddOnOptions({ ...addOnOptions, [key]: value });
    }
  };

  const handleUpdate = () => {
    if (!selectedVendor || selectedVendor === 0) {
      toast.error("Please select a client");
      return;
    }
    toast.loading("Updating...", { id: "addonFeaturesUpdate" });
    const body = {
      industry_id: selectedIndustryId,
      industry_name: selectedIndustryName,
      user_types: JSON.stringify(userTypes),
      addon_features: JSON.stringify(addOnOptions),
      points_sharing: JSON.stringify(pointsSharing),
      redemption_hierarchy: JSON.stringify(redemptionHierarchy),
      reserve_tds_points: JSON.stringify(addOnOptions.reserve_tds),
    };
    if (addOnOptions.dream_gift) body.dream_gift = dreamGiftData;
    updateVendor({
      id: selectedVendor,
      token: sessionStorage.getItem("access_token"),
      body,
    }).finally(() => toast.dismiss("addonFeaturesUpdate"));
  };

  const showContent =
    selectedVendorData && selectedIndustryId != 0 && selectedVendor != 0;

  return (
    <>
      <Toaster />
      <div className="page-content w-100">
        <div className="card p-4 w-100">
          <div className="borderbtm">
            <h5 className="card-title mb-0">Add On Features</h5>
          </div>
          <div className="card-body p-0">
            <div className="row frmds w-100">
              <div className="col-12 col-xl-6 my-2">
                <label>Client</label>
                <select
                  className="js-example-basic-single w-100 rounded-lg shadow-sm shadow-black px-2"
                  value={selectedVendor}
                  onChange={handleVendorChange}
                >
                  <option value={0}>Select Client</option>
                  {vendorsList.map((v) => (
                    <option key={v.company_id} value={v.company_id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-xl-6 my-2">
                <label>Industry</label>
                <select
                  className="js-example-basic-single w-100 rounded-lg shadow-sm shadow-black px-2"
                  value={selectedIndustryId}
                  onChange={handleIndustryChange}
                >
                  <option value={0}>Select Industry</option>
                  {industryList.map((ind) => (
                    <option key={ind.industry_id} value={ind.industry_id}>
                      {ind.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 my-2">
                <label>Users</label>
                <select
                  className="border rounded-lg shadow-sm shadow-black px-2 w-100"
                  defaultValue=""
                  onChange={handleAddUserType}
                >
                  <option value="">Select</option>
                  {allAppUserTypeData?.body?.map((x) =>
                    userTypes.includes(x.user_type) ? null : (
                      <option key={x.user_type} value={x.user_type}>
                        {x.user_type}
                      </option>
                    ),
                  )}
                </select>
                {userTypes.map((user) => (
                  <label className="mt-2 mr-2" key={user}>
                    <span className="btn btn-outline-danger d-flex">
                      {user}
                      <GrFormClose
                        className="w-4 h-4 ml-2"
                        onClick={() => setUserTypes(userTypes.filter((u) => u !== user))}
                      />
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {showContent && (
          <>
            <div className="card my-4 p-4 w-100">
              <div className="borderbtm mb-3">
                <h5 className="card-title mb-0">Feature toggles</h5>
                <p className="text-muted small mb-0">True / False</p>
              </div>
              <div className="d-flex w-full gap-3 flex-wrap">
                {BOOLEAN_ADDON_KEYS.map((key) => (
                  <label key={key} className="border p-2 rounded-lg cursor-pointer">
                    <div
                      onClick={() => handleAddOnToggle(key)}
                      className="d-flex align-items-center"
                    >
                      {addOnOptions[key] ? (
                        <BsCheckCircleFill className="w-3.5 mr-2" />
                      ) : (
                        <BsCircle className="w-3.5 mr-2" />
                      )}
                      <span className="font-semibold">
                        {key.replaceAll("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </label>
                ))}
                <label
                  className={`transition-all rounded-lg border overflow-hidden ${addOnOptions.monthly_tds?.taken ? "!border-green-700" : ""}`}
                >
                  <div className="flex flex-col w-full">
                    <div
                      className={`px-2 py-2 d-flex align-items-center cursor-pointer ${addOnOptions.monthly_tds?.taken ? "bg-green-200 border-b !border-green-700" : ""}`}
                      onClick={() =>
                        setAddOnOptions({
                          ...addOnOptions,
                          monthly_tds: {
                            ...addOnOptions.monthly_tds,
                            taken: !addOnOptions.monthly_tds?.taken,
                          },
                        })
                      }
                    >
                      {addOnOptions.monthly_tds?.taken ? (
                        <BsCheckCircleFill className="w-3.5 mr-2" />
                      ) : (
                        <BsCircle className="w-3.5 mr-2" />
                      )}
                      <span className="font-semibold">ENABLE MONTHLY TDS</span>
                    </div>
                    <div className="px-2 py-2">
                      <div
                        className={`rounded-lg cursor-pointer d-flex align-items-center px-2 py-1 ${addOnOptions.monthly_tds?.only_scheme ? "bg-slate-200" : ""}`}
                        onClick={() =>
                          setAddOnOptions({
                            ...addOnOptions,
                            monthly_tds: {
                              ...addOnOptions.monthly_tds,
                              only_scheme: !addOnOptions.monthly_tds?.only_scheme,
                            },
                          })
                        }
                      >
                        Only scheme:{" "}
                        {addOnOptions.monthly_tds?.only_scheme ? (
                          <BsCheckCircleFill className="w-3.5 ml-2" />
                        ) : (
                          <BsCircle className="w-3.5 ml-2" />
                        )}
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="card my-4 p-4 w-100">
              <div className="borderbtm mb-3">
                <h5 className="card-title mb-0">Main / Scheme type</h5>
                <p className="text-muted small mb-0">
                  Enable Kafka Scan to configure scanning &amp; loyalty rules type
                </p>
              </div>
              <div className="d-flex w-full gap-3 flex-wrap mb-3">
                {SCAN_SECTION_TOGGLES.map(({ key, label }) => (
                  <label key={key} className="border p-2 rounded-lg cursor-pointer">
                    <div
                      onClick={() => handleAddOnToggle(key)}
                      className="d-flex align-items-center"
                    >
                      {addOnOptions[key] ? (
                        <BsCheckCircleFill className="w-3.5 mr-2 text-primary" />
                      ) : (
                        <BsCircle className="w-3.5 mr-2" />
                      )}
                      <span className="font-semibold">{label}</span>
                    </div>
                  </label>
                ))}
              </div>
              {addOnOptions.kafka_scan && (
                <div className="row border-top pt-3">
                  {MAIN_SCHEME_TYPE_FIELDS.map(({ key, label }) => (
                    <div key={key} className="col-12 col-md-6 mb-3">
                      <p className="font-semibold mb-2">{label}</p>
                      <div className="d-flex gap-3 flex-wrap">
                        {MAIN_SCHEME_OPTIONS.map((opt) => {
                          const selected = addOnOptions[key] === opt;
                          return (
                            <label
                              key={opt}
                              className={`border p-2 rounded-lg cursor-pointer d-flex align-items-center ${selected ? "border-primary bg-light" : ""}`}
                              onClick={() => handleMainSchemeType(key, opt)}
                            >
                              {selected ? (
                                <BsCheckCircleFill className="w-3.5 mr-2 text-primary" />
                              ) : (
                                <BsCircle className="w-3.5 mr-2" />
                              )}
                              <span className="font-semibold text-uppercase">{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                      {key === "scanning_type" && (
                        <p className="text-muted small mt-2 mb-0">
                          Auto-sets membership flags →{" "}
                          {addOnOptions.scanning_type === "main" && (
                            <span>
                              <strong>membership</strong> ✓ &nbsp; scheme_membership ✗
                            </span>
                          )}
                          {addOnOptions.scanning_type === "scheme" && (
                            <span>
                              membership ✗ &nbsp; <strong>scheme_membership</strong> ✓
                            </span>
                          )}
                          {addOnOptions.scanning_type === "both" && (
                            <span>
                              <strong>membership</strong> ✓ &nbsp;{" "}
                              <strong>scheme_membership</strong> ✓
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card my-4 p-4 w-100">
              <div className="borderbtm mb-3">
                <h5 className="card-title mb-0">Advanced setup</h5>
                <p className="text-muted small mb-0">Opens a configuration popup</p>
              </div>
              <DreamGiftModal
                open={dreamGiftPopUp}
                setOpen={setDreamGiftPopUp}
                dreamGiftData={dreamGiftData}
                setDreamGiftData={setDreamGiftData}
                userTypes={userTypes}
              />
              <PointSharing
                open={pointSharingPopUp}
                setOpen={setPointSharingPopUp}
                userTypes={userTypes}
                handleAddPointSharing={setPointsSharing}
                pointsSharing={pointsSharing}
              />
              <RedemptionApprovalHierarchay
                open={redemptionHierarchyPopUp}
                setOpen={setRedemptionHierarchyPopUp}
                userTypes={userTypes}
                handleRedemptionHierarchy={setRedemptionHierarchy}
                redemptionHierarchy={redemptionHierarchy}
              />
              <ReserveTdsModal
                open={reserveTdsPopUp}
                setOpen={setReserveTdsPopUp}
                addOnOptions={addOnOptions}
                setAddOnOptions={setAddOnOptions}
                userTypes={userTypes}
              />
              <div className="d-flex w-full gap-3 flex-wrap">
                <label className="border p-2 rounded-lg cursor-pointer">
                  <div
                    onClick={() => handleAddOnToggle("dream_gift")}
                    className="d-flex align-items-center"
                  >
                    {addOnOptions.dream_gift ? (
                      <BsCheckCircleFill className="w-3.5 mr-2" />
                    ) : (
                      <BsCircle className="w-3.5 mr-2" />
                    )}
                    <span className="font-semibold">DREAM GIFT</span>
                  </div>
                  {addOnOptions.dream_gift && (
                    <div className="small mt-1 pl-4">
                      users: {dreamGiftData.users.join(", ") || "—"}
                      <br />
                      target: {dreamGiftData.target}
                      {dreamGiftData.target === "value" && (
                        <>
                          <br />
                          {dreamGiftData.percentage}%
                        </>
                      )}
                    </div>
                  )}
                </label>
                <label className="border p-2 rounded-lg cursor-pointer">
                  <span className="font-semibold" onClick={() => setPointSharingPopUp(true)}>
                    POINT SHARING
                  </span>
                </label>
                <label className="border p-2 rounded-lg cursor-pointer">
                  <span className="font-semibold" onClick={() => setRedemptionHierarchyPopUp(true)}>
                    REDEMPTION HIERARCHY
                  </span>
                </label>
                <label className="border p-2 rounded-lg cursor-pointer">
                  <span className="font-semibold" onClick={() => setReserveTdsPopUp(true)}>
                    RESERVE TDS PERCENTAGE
                  </span>
                </label>
              </div>
            </div>
          </>
        )}

        <div className="card p-4 w-100">
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-outline-primary btnall"
              onClick={handleUpdate}
              disabled={updateDisabled}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const DreamGiftModal = ({ open, setOpen, dreamGiftData, setDreamGiftData, userTypes }) => {
  const [users, setUsers] = useState(dreamGiftData.users);
  const [target, setTarget] = useState(dreamGiftData.target);
  const [percentage, setPercentage] = useState(dreamGiftData.percentage);
  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <div style={{ width: 400, overflow: Hidden }}>
        <DialogTitle>Dream Gift Setup</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel>Usertype</InputLabel>
            <Select multiple value={users} onChange={(e) => setUsers(e.target.value)} renderValue={(s) => s.join(", ")}>
              {userTypes.map((x) => (
                <MenuItem key={x} value={x}>{x}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth className="mt-3">
            <InputLabel>Target</InputLabel>
            <Select value={target} onChange={(e) => setTarget(e.target.value)}>
              <MenuItem value="point">point</MenuItem>
              <MenuItem value="value">value</MenuItem>
            </Select>
          </FormControl>
          {target === "value" && (
            <TextField className="mt-3" label="percentage" fullWidth value={percentage} onChange={(e) => setPercentage(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="error" variant="contained">Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!users.length) return;
              setDreamGiftData({ users, target, percentage: target === "point" ? null : percentage });
              setOpen(false);
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

const ReserveTdsModal = ({ open, setOpen, addOnOptions, setAddOnOptions, userTypes }) => {
  const setDoc = (userType, doc) => {
    setAddOnOptions((prev) => {
      const docs = prev.reserve_tds?.required_documents || {};
      const ut = docs[userType] || { aadhar: false, pan: false, gstin: false };
      return {
        ...prev,
        reserve_tds: {
          ...prev.reserve_tds,
          required_documents: { ...docs, [userType]: { ...ut, [doc]: !ut[doc] } },
        },
      };
    });
  };
  const setPct = (key, val) => {
    setAddOnOptions((prev) => ({
      ...prev,
      reserve_tds: {
        ...prev.reserve_tds,
        tds_reserve_percentage: { ...prev.reserve_tds?.tds_reserve_percentage, [key]: val },
      },
    }));
  };
  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <div style={{ width: 450 }}>
        <DialogTitle>Reserve TDS</DialogTitle>
        <DialogContent>
          {userTypes.map((ut) => (
            <div key={ut} className="mb-2 border p-2">
              <b>{ut}</b>
              {["aadhar", "pan", "gstin"].map((doc) => (
                <span key={doc} className="mx-2 cursor-pointer" onClick={() => setDoc(ut, doc)}>
                  {addOnOptions?.reserve_tds?.required_documents?.[ut]?.[doc] ? "☑" : "○"} {doc}
                </span>
              ))}
            </div>
          ))}
          <TextField
            className="mt-2"
            fullWidth
            type="number"
            label="Before doc (%)"
            value={addOnOptions?.reserve_tds?.tds_reserve_percentage?.before_document_submission || 0}
            onChange={(e) => setPct("before_document_submission", Number(e.target.value))}
          />
          <TextField
            className="mt-2"
            fullWidth
            type="number"
            label="After doc (%)"
            value={addOnOptions?.reserve_tds?.tds_reserve_percentage?.after_document_submission || 0}
            onChange={(e) => setPct("after_document_submission", Number(e.target.value))}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)}>Save</Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default RulesAndFeatures;
